import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Edit2, Loader2, MailPlus, Plus, Search, ToggleLeft, ToggleRight } from "lucide-react"
import { toast } from "sonner"
import { getTenants, type Tenant } from "shared/api/tenantService"
import { Badge } from "shared/ui/badge"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Switch } from "shared/ui/switch"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { Textarea } from "shared/ui/textarea"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"
import {
  createMailTemplate,
  searchMailTemplates,
  setMailTemplateActive,
  updateMailTemplate,
  type MailTemplateCategory,
  type MailTemplateDto,
  type MailTemplateStatusFilter,
  type MailTemplateUpsertRequest,
} from "modules/admin/api/emailTemplateService"

interface EmailTemplateConfigurationProps {
  currentUser?: {
    name?: string
    tenantId?: string
    tenants?: Array<{ tenantId?: string; tenantName?: string; name?: string }>
  }
}

type FormState = {
  tenantId: string
  mailName: string
  mailCode: string
  category: MailTemplateCategory
  subject: string
  body: string
  isActive: boolean
}

type TemplateVariable = {
  token: string
  label: string
}

const CATEGORY_OPTIONS: Array<{ value: MailTemplateCategory; label: string }> = [
  { value: "APPROVAL", label: "Approval" },
  { value: "REVISE", label: "Revise" },
  { value: "NOTIFICATION", label: "Notification" },
]

const STATUS_OPTIONS: Array<{ value: MailTemplateStatusFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
]

const CATEGORY_BADGE_STYLES: Record<MailTemplateCategory, string> = {
  APPROVAL: "bg-emerald-100 text-emerald-700",
  REVISE: "bg-amber-100 text-amber-700",
  NOTIFICATION: "bg-sky-100 text-sky-700",
}

const CATEGORY_VARIABLES: Record<MailTemplateCategory, TemplateVariable[]> = {
  APPROVAL: [
    { token: "{permit_number}", label: "เลขที่ Permit" },
    { token: "{work_type}", label: "ประเภทงาน" },
    { token: "{work_description}", label: "รายละเอียดงาน" },
    { token: "{requester_name}", label: "ชื่อผู้ขอ Permit" },
    { token: "{supervisor_name}", label: "ชื่อผู้ควบคุมงาน" },
    { token: "{company_name}", label: "ชื่อบริษัท" },
    { token: "{location}", label: "สถานที่ทำงาน" },
    { token: "{work_date}", label: "วันที่ขอเข้าปฏิบัติงาน" },
  ],
  REVISE: [
    { token: "{permit_number}", label: "เลขที่ Permit" },
    { token: "{work_type}", label: "ประเภทงาน" },
    { token: "{work_description}", label: "รายละเอียดงาน" },
    { token: "{requester_name}", label: "ชื่อผู้ขอ Permit" },
    { token: "{supervisor_name}", label: "ชื่อผู้ควบคุมงาน" },
    { token: "{company_name}", label: "ชื่อบริษัท" },
    { token: "{location}", label: "สถานที่ทำงาน" },
    { token: "{work_date}", label: "วันที่ขอเข้าปฏิบัติงาน" },
    { token: "{revised_by}", label: "ผู้ที่แจ้งแก้ไข" },
    { token: "{revise_reason}", label: "เหตุผล" },
  ],
  NOTIFICATION: [
    { token: "{permit_number}", label: "เลขที่ Permit" },
    { token: "{work_type}", label: "ประเภทงาน" },
    { token: "{work_description}", label: "รายละเอียดงาน" },
    { token: "{requester_name}", label: "ชื่อผู้ขอ Permit" },
    { token: "{supervisor_name}", label: "ชื่อผู้ควบคุมงาน" },
    { token: "{company_name}", label: "ชื่อบริษัท" },
    { token: "{location}", label: "สถานที่ทำงาน" },
    { token: "{work_date}", label: "วันที่ขอเข้าปฏิบัติงาน" },
    { token: "{note}", label: "หมายเหตุ" },
  ],
}

const initialForm = (tenantId: string): FormState => ({
  tenantId,
  mailName: "",
  mailCode: "",
  category: "APPROVAL",
  subject: "",
  body: "",
  isActive: true,
})

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ??
  error?.response?.data ??
  error?.message ??
  fallback

const normalizeTemplateCode = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_")

const buildTenantOptionsFromCurrentUser = (currentUser?: EmailTemplateConfigurationProps["currentUser"]) => {
  const rawTenants = currentUser?.tenants
  if (!Array.isArray(rawTenants)) {
    return []
  }

  return rawTenants
    .map((tenant) => ({
      tenantId: tenant.tenantId ?? "",
      name: tenant.tenantName ?? tenant.name ?? tenant.tenantId ?? "",
    }))
    .filter((tenant) => tenant.tenantId)
}

export default function EmailTemplateConfiguration({ currentUser }: EmailTemplateConfigurationProps) {
  const seededTenantOptions = useMemo(() => buildTenantOptionsFromCurrentUser(currentUser), [currentUser])
  const [tenantOptions, setTenantOptions] = useState<Array<Pick<Tenant, "tenantId" | "name">>>(
    seededTenantOptions.length > 0
      ? seededTenantOptions
      : [{ tenantId: currentUser?.tenantId ?? DEFAULT_TENANT_ID, name: currentUser?.tenantId ?? DEFAULT_TENANT_ID }],
  )
  const [selectedTenantId, setSelectedTenantId] = useState<string>(currentUser?.tenantId ?? DEFAULT_TENANT_ID)
  const [items, setItems] = useState<MailTemplateDto[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<MailTemplateStatusFilter>("all")
  const [categoryFilter, setCategoryFilter] = useState<MailTemplateCategory | "all">("all")
  const [isLoading, setIsLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editing, setEditing] = useState<MailTemplateDto | null>(null)
  const [form, setForm] = useState<FormState>(initialForm(currentUser?.tenantId ?? DEFAULT_TENANT_ID))
  const [activeEditorField, setActiveEditorField] = useState<"subject" | "body">("body")
  const subjectRef = useRef<HTMLInputElement | null>(null)
  const bodyRef = useRef<HTMLTextAreaElement | null>(null)

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems, pageSize])
  const availableVariables = useMemo(() => CATEGORY_VARIABLES[form.category], [form.category])

  useEffect(() => {
    if (seededTenantOptions.length === 0) {
      return
    }
    setTenantOptions(seededTenantOptions)
    setSelectedTenantId((prev) => prev || seededTenantOptions[0]?.tenantId || DEFAULT_TENANT_ID)
  }, [seededTenantOptions])

  useEffect(() => {
    if (seededTenantOptions.length > 0) {
      return
    }

    let isMounted = true
    getTenants()
      .then((result) => {
        if (!isMounted || !Array.isArray(result) || result.length === 0) {
          return
        }
        const nextOptions = result.map((tenant) => ({
          tenantId: tenant.tenantId,
          name: tenant.name,
        }))
        setTenantOptions(nextOptions)
        setSelectedTenantId((prev) => {
          if (prev && nextOptions.some((option) => option.tenantId === prev)) {
            return prev
          }
          return nextOptions[0]?.tenantId ?? DEFAULT_TENANT_ID
        })
      })
      .catch(() => {
        // Keep fallback tenant option when tenant API is unavailable.
      })

    return () => {
      isMounted = false
    }
  }, [seededTenantOptions.length])

  const loadTemplates = useCallback(
    async (nextPage = 1) => {
      if (!selectedTenantId) {
        setItems([])
        setTotalItems(0)
        return
      }

      setIsLoading(true)
      try {
        const result = await searchMailTemplates({
          tenantId: selectedTenantId,
          search: searchTerm.trim() || undefined,
          category: categoryFilter,
          status: statusFilter,
          page: nextPage,
          pageSize,
        })
        setItems(result.items ?? [])
        setTotalItems(result.totalItems ?? result.items.length)
        setPage(result.pageNumber ?? nextPage)
      } catch (error) {
        console.error(error)
        toast.error(getErrorMessage(error, "Failed to load email templates"))
      } finally {
        setIsLoading(false)
      }
    },
    [categoryFilter, pageSize, searchTerm, selectedTenantId, statusFilter],
  )

  useEffect(() => {
    loadTemplates(1)
  }, [loadTemplates])

  const resetForm = useCallback(
    (tenantId?: string) => {
      const resolvedTenantId = tenantId || selectedTenantId || DEFAULT_TENANT_ID
      setEditing(null)
      setForm(initialForm(resolvedTenantId))
      setActiveEditorField("body")
    },
    [selectedTenantId],
  )

  const handleOpenCreate = () => {
    resetForm(selectedTenantId)
    setDialogOpen(true)
  }

  const handleOpenEdit = (item: MailTemplateDto) => {
    setEditing(item)
    setForm({
      tenantId: item.tenantId ?? selectedTenantId ?? DEFAULT_TENANT_ID,
      mailName: item.mailName ?? "",
      mailCode: item.mailCode ?? "",
      category: item.category,
      subject: item.subject ?? "",
      body: item.body ?? "",
      isActive: item.isActive,
    })
    setActiveEditorField("body")
    setDialogOpen(true)
  }

  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      resetForm(selectedTenantId)
    }
  }

  const insertTokenAtCursor = (token: string) => {
    const target = activeEditorField === "subject" ? subjectRef.current : bodyRef.current
    const fieldKey = activeEditorField
    const currentValue = form[fieldKey]

    if (!target) {
      setForm((prev) => ({
        ...prev,
        [fieldKey]: `${currentValue}${currentValue ? " " : ""}${token}`,
      }))
      return
    }

    const selectionStart = target.selectionStart ?? currentValue.length
    const selectionEnd = target.selectionEnd ?? currentValue.length
    const nextValue = `${currentValue.slice(0, selectionStart)}${token}${currentValue.slice(selectionEnd)}`

    setForm((prev) => ({ ...prev, [fieldKey]: nextValue }))

    window.setTimeout(() => {
      target.focus()
      const nextCursor = selectionStart + token.length
      target.setSelectionRange(nextCursor, nextCursor)
    }, 0)
  }

  const handleSubmit = async () => {
    if (!form.tenantId) {
      toast.error("Tenant is required")
      return
    }
    if (!form.mailName.trim()) {
      toast.error("Template name is required")
      return
    }
    if (!form.mailCode.trim()) {
      toast.error("Template code is required")
      return
    }
    if (!form.subject.trim()) {
      toast.error("Subject is required")
      return
    }
    if (!form.body.trim()) {
      toast.error("Email body is required")
      return
    }

    const payload: MailTemplateUpsertRequest = {
      tenantId: form.tenantId,
      mailName: form.mailName.trim(),
      mailCode: normalizeTemplateCode(form.mailCode),
      category: form.category,
      subject: form.subject.trim(),
      body: form.body.trim(),
      isActive: form.isActive,
    }

    setIsSubmitting(true)
    try {
      if (editing) {
        await updateMailTemplate(editing.mailId, payload)
        toast.success("Updated email template")
      } else {
        await createMailTemplate(payload)
        toast.success("Created email template")
      }
      setDialogOpen(false)
      resetForm(selectedTenantId)
      loadTemplates(editing ? page : 1)
    } catch (error) {
      console.error(error)
      toast.error(getErrorMessage(error, "Failed to save email template"))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleActive = async (item: MailTemplateDto) => {
    try {
      await setMailTemplateActive(item.mailId, !item.isActive)
      toast.success(`Template ${item.isActive ? "deactivated" : "activated"}`)
      loadTemplates(page)
    } catch (error) {
      console.error(error)
      toast.error(getErrorMessage(error, "Failed to update template status"))
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <MailPlus className="h-5 w-5 text-indigo-600" />
              Email Template Configuration
            </CardTitle>
            <p className="text-sm text-gray-500">
              Manage email subject and body templates per tenant for approval, revise, and notification flows.
            </p>
          </div>
          <Button onClick={handleOpenCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Email Template
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <div className="space-y-2">
              <Label>Tenant</Label>
              <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenantOptions.map((tenant) => (
                    <SelectItem key={tenant.tenantId} value={tenant.tenantId}>
                      {tenant.name || tenant.tenantId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={categoryFilter} onValueChange={(value) => setCategoryFilter(value as MailTemplateCategory | "all")}>
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {CATEGORY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as MailTemplateStatusFilter)}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onKeyDown={(event) => event.key === "Enter" && loadTemplates(1)}
                  placeholder="Code / name / subject"
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-500">
              {selectedTenantId ? `Tenant: ${selectedTenantId}` : "Select tenant to manage templates"}
            </p>
            <Button variant="outline" onClick={() => loadTemplates(1)} disabled={isLoading}>
              Refresh
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading email templates...
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead className="w-24 text-center">Status</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-6 text-center text-sm text-gray-500">
                        No email templates found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => (
                      <TableRow key={item.mailId}>
                        <TableCell className="font-medium">{item.mailName}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.mailCode}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${CATEGORY_BADGE_STYLES[item.category]}`}>
                            {item.category}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-md truncate">{item.subject}</TableCell>
                        <TableCell className="text-center">
                          <button
                            type="button"
                            onClick={() => handleToggleActive(item)}
                            className="inline-flex items-center justify-center"
                            aria-label={item.isActive ? "Deactivate template" : "Activate template"}
                          >
                            {item.isActive ? (
                              <ToggleRight className="h-6 w-6 text-emerald-600" />
                            ) : (
                              <ToggleLeft className="h-6 w-6 text-gray-400" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" onClick={() => handleOpenEdit(item)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>Total {totalItems} template(s)</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1 || isLoading} onClick={() => loadTemplates(page - 1)}>
                Previous
              </Button>
              <span>
                Page {page} / {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={page >= totalPages || isLoading} onClick={() => loadTemplates(page + 1)}>
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Email Template" : "Create New Email Template"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={form.mailName}
                  onChange={(event) => setForm((prev) => ({ ...prev, mailName: event.target.value }))}
                  placeholder="Enter template name..."
                />
              </div>
              <div className="space-y-2">
                <Label>Template Code *</Label>
                <Input
                  value={form.mailCode}
                  onChange={(event) => setForm((prev) => ({ ...prev, mailCode: normalizeTemplateCode(event.target.value) }))}
                  placeholder="Enter template code..."
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={(value) => setForm((prev) => ({ ...prev, category: value as MailTemplateCategory }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Tenant / Site *</Label>
                <Select value={form.tenantId} onValueChange={(value) => setForm((prev) => ({ ...prev, tenantId: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select tenant / site" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenantOptions.map((tenant) => (
                      <SelectItem key={tenant.tenantId} value={tenant.tenantId}>
                        {tenant.name || tenant.tenantId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email Subject *</Label>
              <Input
                ref={subjectRef}
                value={form.subject}
                onFocus={() => setActiveEditorField("subject")}
                onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
                placeholder="Enter email subject..."
              />
            </div>

            <div className="space-y-3 rounded-md border bg-slate-50 p-4">
              <div>
                <p className="text-sm font-medium text-slate-900">Available Variables</p>
                <p className="text-xs text-slate-500">
                  Click a variable to insert it into the currently focused subject or body field.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {availableVariables.map((variable) => (
                  <button
                    key={`${form.category}_${variable.token}`}
                    type="button"
                    onClick={() => insertTokenAtCursor(variable.token)}
                    className="rounded-md border bg-white px-3 py-2 text-left text-xs text-indigo-700 hover:border-indigo-300 hover:bg-indigo-50"
                  >
                    <div className="font-mono text-[11px]">{variable.token}</div>
                    <div className="mt-1 text-slate-600">{variable.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email Body (HTML) *</Label>
              <Textarea
                ref={bodyRef}
                rows={14}
                value={form.body}
                onFocus={() => setActiveEditorField("body")}
                onChange={(event) => setForm((prev) => ({ ...prev, body: event.target.value }))}
                placeholder="<html>...</html>"
              />
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-xs text-gray-500">Inactive templates remain in the list but should not be used for sending.</p>
              </div>
              <Switch
                checked={form.isActive}
                onCheckedChange={(checked) => setForm((prev) => ({ ...prev, isActive: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleDialogChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
